
import {Request, UserModel} from '../data';
import {Utils} from "../common/Utils";

/**
 * ARGO collection / API endpoint that handles user datasets
 * @type {string}
 */
const COLLECTION = 'users';

/**
 * Class which represents the current user by using the ARGO API call /users/@me. This provides functions to
 * access data from the current user, update user data and provide functions to reset password,
 * acceptTerms and more.
 *
 * Uses the {@link Request} class with special configuration to implement user functions.
 *
 */
export class UserIdentity extends UserModel {

  constructor(config) {

    super($.extend({
      on: {
        'sync:done': '_handleSyncDone',
        'sync:error': '_handleSyncError'
      }
    }, config));

    /**
     * Tenant name from the current user.
     * @type {string}
     */
    this.tenant = null;
    /**
     * Indicates authorization state.
     * @type {boolean}
     */
    this.authorized = false;
    /**
     * Array of callback function to be invoked after identity process.
     * @type {Array<Function>}
     * @private
     */
    this._authQueue = [];
    this.once('changeauth', () => {
      this._authQueue.forEach((fn) => {
        fn(this, this.authorized);
      });
    });
    if (!config.data) {
      this.fetchData().catch(() => {});
    }
  }

  /**
   *
   * @private
   */
  _handleSyncDone() {
    this.authorized = true;
    this.emit('changeauth');
  }

  _handleSyncError() {
    this.authorized = false;
    this.emit('changeauth');
  }

  /**
   * Fetch data from current user by using "@me"
   * @return {Promise<any> | * | Promise}
   */
  fetchData() {
    return this.fetchById('@me', {handleUnauthorized: false})
  }

  /**
   * Register a callback function to be called after user' identity
   * was loaded. The Callback function will also be called when
   * the identity process has been finished.
   * @param {Function} fn - callback function, arguments: UserIdentity, true|false (authorization state)
   * @return {UserIdentity}
   */
  afterAuth(fn) {
    // add to queue if still in loading process
    if (this.isLoading()) {
      this._authQueue.push(fn);
      return this;
    }

    // call immediately if user state is already given
    fn(this, this.authorized);

    return this;
  }

  /**
   * Login user with current credentials.
   * @param username
   * @param password
   * @return {Promise<any>}
   */
  login(username, password) {
    let resp = new Request({url: '/login'})
      .addHeader('Authorization', 'Basic ' + btoa( username + ':' + password))
      .post()
      .then(() => {
        let returnUrl = sessionStorage.getItem('returnUrl');
        if (returnUrl) {
          sessionStorage.removeItem('returnUrl');

          if (returnUrl.startsWith('/')) {
            this.app.redirect(returnUrl);
            // cancel propagation, because redirect is asynchronous
            resp.promise.cancel();
            return;
          }
        }
        return this.fetchData()
      });
    return resp;
  }

  /**
   * Logoff current user.
   * @return {Promise<any>}
   */
  logout() {
    return new Request({url: '/logoff'})
      .post()
      .then(() => {
        this.authorized = false;
        this.emit('changeauth');
      });
  }

  /**
   * Returns true if current user is authorized
   * @return {boolean}
   */
  isAuthorized() {
    return this.authorized;
  }

  /**
   * Returns the shortcut token for the user state: "@" for authorized
   * and "?" for anonymous.
   * @return {string}
   */
  getRoleToken() {
    return this.isAuthorized() ? '@' : '?';
  }

  /**
   * Returns role from current logged in user or null if anonymous user.
   * @return {String|null}
   */
  getRole() {
    if (this.isAuthorized() && this.properties.roles) {
      return this.properties.roles[0];
    }
    return null;
  }

  /**
   * Searching user's role in given key-value map and return the value
   * if exists. Also checks for role token.
   * Return null if no key matches any role.
   * @param map
   * @return {*}
   */
  findRoleInMap(map) {
    let role = this.getRole();
    if (this.isAuthorized() && Utils.isString(role)) {
      role = role.toLowerCase();
      if (map.hasOwnProperty(role)) {
        return map[role];
      }
    }
    return map[this.getRoleToken()] || null;
  }

  /**
   * Check if user's role matches the given argument.
   * @param {Array<String>|String} list
   * @return {boolean}
   */
  matchRole(list) {
    if (Utils.isString(list)) {
      return list === this.getRoleToken() || list.toUpperCase() === this.getRole();
    }
    let token = this.getRoleToken();
    return list.some((v) => {
      return v === token || v.toUpperCase() === this.getRole();
    });
  }

  /**
   * Register a new user with given properties. Normally a confirmation
   * email will be sent to the user.
   * @param {Object} props - properties to be send to the backend
   * @return {Promise<any>}
   */
  register(props) {
    return new Request({collection: this._collection})
      .post('register', props)
      .then(() => {
        this.authorized = true;
        this.app.emit('user.changeauth');
      });
  }

  /**
   * Confirm a user account with given confirmation token.
   * The user will be automatically loggedin (by backend).
   * @param {String} id - confirmation token
   * @return {Promise<any>}
   */
  acceptTerms(id) {
    return new Request({url: `/${this._collection}/acceptTermsAndConditions/{id}`})
      .post(id)
      .then(() => this.fetchData());
  }

  /**
   * Stores the given email address to the account. The backend sends an email
   * with a verification link to enable the new email.
   * @param {String} email - user's new mail address
   * @param {String} password - user's current password
   * @return {Promise}
   */
  requestChangeEmailAddress(email, password) {
    return new Request({
      url: `/${this._collection}/{id}/changeEmailAddress`,
      contentType: 'application/x-www-form-urlencoded'
    })
      .post(this.getId(), {
        email,
        password
      });
  }

  /**
   * Verify user's email address with the given token id. The backend changes user's email address.
   * @param {String} id - reset token id (from the change email request mail)
   * @return {Promise}
   */
  verifyEmailAddressByToken(id) {
    return new Request({
      url: `/${this._collection}/changeEmailAddress/{id}`,
      contentType: 'application/x-www-form-urlencoded'
    })
      .post(id);
  }

  /**
   * Send an password reset email to the user with given mail address.
   * @param {String} email - user's mail address
   * @return {Promise}
   */
  sendResetPasswordLink(email) {
    return new Request({collection: this._collection})
      .post('resetPassword', {
        email
      });
  }

  /**
   * Update user's password for the account with the given reset token id.
   * @param {String} id - reset token id (from the reset password mail)
   * @param {String} newPassword - new password for the user account
   * @return {Promise}
   */
  changePasswordByToken(id, newPassword) {
    return new Request({
      url: `/${this._collection}/resetPassword/{id}`,
      contentType: 'application/x-www-form-urlencoded'
    })
      .post(id, {
        password: newPassword
      });
  }

  /**
   * Synchronize user's personal data with the backend and refreshes
   * the cached user data on backend side.
   * @param options
   * @return {Promise<any>}
   */
  save(options = {}) {
    // other update data -> patch request
    return this._createRequest({url: '/{collection}/{id}/personalData'})
      .patch(this.properties.id, this._personalData)
      /*.then(() => {
        return new Request({
            url: `/${this._collection}/{id}?reload=true`
          })
          .get('@me');
      })*/
      .then(() => {
        this._isLoading = false;
        if (options.silent !== true) {
          this.emit('sync:done', this);
        }
      })
      .catch((err) => {
        this._isLoading = false;
        if (options.silent !== true) {
          this.emit('sync:error', this, err);
        }
        throw err;
      });
  }

  /**
   * Changes user's password.
   * @param {options} data
   * @param {string} data.username
   * @param {string} data.oldPassword
   * @param {string} data.newPassword
   * @return {*}
   */
  changePassword(data) {
    return new Request({
      url: `/${this._collection}/password`,
      contentType: 'application/x-www-form-urlencoded'
    })
      .post(data);
  }

  /**
   * Updates the locale property from the current user.
   * @param {String} locale
   * @return {Promise}
   */
  updateLocale(locale) {
    return this._createRequest({
        url: '/{collection}/{id}/locale',
        contentType: 'text/plain'
      })
      .put(this.properties.id, locale);
  }

  /**
   * Checks if the user is connected with a filter data rules
   * with given key and a value exists.
   * @param {String} key - name of the property filter
   * @return {boolean}
   */
  hasPropertyFilterData(key) {
    return this.has('propertyFilterData') && this.properties.propertyFilterData[key] &&
      this.properties.propertyFilterData[key].length > 0;
  }

  /**
   * Returns the filter for the given property filter.
   * @param {String} key - name of the property filter
   * @return {Array<*>|null}
   */
  getPropertyFilterData(key) {
    return this.hasPropertyFilterData(key) ? this.properties.propertyFilterData[key] : null;
  }

  /**
   * For Admins only: Switches the current session to the user with
   * given username.
   * @param {String} username - user's name to switch to
   * @return {Promise}
   */
  switch(username) {
    return new Request({
        url: `/${this._collection}/switch`,
        contentType: 'application/x-www-form-urlencoded'
      })
      .post({
        username
      });
  }

  /**
   * For Admins only: Exit user's session and go back to the admin session.‚
   * @return {Promise}
   */
  exitSwitch() {
    return new Request({
        url: `/${this._collection}/switch/exit`,
        contentType: false
      })
      .post();
  }

  /**
   * Before parse callback: extract user's id
   * @param data
   * @param options
   */
  onBeforeParse(data, options = {}) {

    // special case for "@me" response -> extract id
    if (!this._id && data.id && !data._links) {
      this._id = data.id;
    }

    if (data.fullname) {
      this.tenant = data.fullname.split('/').shift();
    }

    super.onBeforeParse(data, options);
  }
}
